import { Entity } from '@api/api/entity-schema';
import { BrandAndProduct } from '@api/api/theme-overview';
import { formatTicker } from '@api/mappers/formatTicker';
import { Icon } from '@design/components/icon/Icon';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { NonLinearStepper } from '@terminal/components/non-linear-stepper/NonLinearStepper';
import { NonLinearStepperRevamp } from '@terminal/components/non-linear-stepper/NonLinearStepperRevamp';
import { RankSection } from '@terminal/components/rank-section/RankSection';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { GeoSourceEvidenceSection } from '@terminal/widgets/company-to-geo-widget/tabs/components/source-evidence-section/GeoSourceEvidenceSection';
import { SourceEvidenceSectionRevamp } from '@terminal/widgets/company-to-geo-widget/tabs/components/source-evidence-section/SourceEvidenceSectionRevamp';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { useTranslation } from 'react-i18next';

import styles from './BrandAndProductDrawerContent.module.scss';

export interface BrandAndProductDrawerContentProps {
  entity: Entity;
  logoUrl: string;
  productAndBrand: BrandAndProduct;
  title?: string;
}

export const BrandAndProductDrawerContent = ({
  entity,
  logoUrl,
  productAndBrand,
  title,
}: BrandAndProductDrawerContentProps) => {
  const { t } = useTranslation(['widget']);
  const isConnectionsDrawersOverhaul = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5359-connections-drawers-overhaul')
  );
  const companyName = entity.name_short || entity.name;
  const { product, relationship } = productAndBrand;

  if (isConnectionsDrawersOverhaul) {
    return (
      <div className={styles.root}>
        <ErrorBoundaryWrapper>
          <NonLinearStepperRevamp
            activeIndex={0}
            iconSize={32}
            graphNode={{ id: entity.tag, type: 'company' }}
            steps={[
              {
                logoUrl,
                hoverLabel: formatTicker(entity),
                title: companyName,
                connectorLabel: t('widget:assetOverview.knowledgeGraph.offers'),
              },
              {
                iconColor: 'var(--crimson-9)',
                hoverLabel: product,
                title: product,
              },
            ]}
          />
        </ErrorBoundaryWrapper>
        <ErrorBoundaryWrapper>
          <RankSection
            subjectName={product}
            targetName={companyName}
            rank={relationship.rank}
            rankChange={relationship.rank_change}
            description={relationship.description}
          />
        </ErrorBoundaryWrapper>
        <ErrorBoundaryWrapper>
          <SourceEvidenceSectionRevamp
            evidence={relationship.evidence}
            sourceEvidence={relationship.source_evidence}
          />
        </ErrorBoundaryWrapper>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.sectionRow} data-testid="product-stepper-section">
        {title && <div className={styles.titleMedium}>{title}</div>}
        <div className={styles.stepperWrapper}>
          <NonLinearStepper
            activeIndex={0}
            steps={[
              {
                logoUrl: logoUrl,
                hoverLabel: formatTicker(entity),
                connectorLabel: t('widget:assetOverview.knowledgeGraph.offers'),
                isSimple: false,
                highlightConnector: true,
              },
              {
                imgNode: (
                  <Icon
                    className={styles.styledIcon}
                    iconName="Award"
                    size={18}
                  />
                ),
                iconColor: 'var(--kg-brand)',
                hoverLabel: '',
                highlightConnector: true,
              },
            ]}
            iconSize={23}
          />
        </div>
        <div className={styles.bodySmall}>{relationship.description}</div>
      </div>
      <div className={styles.sectionRow}>
        <div className={styles.titleBase}>
          {t(
            'widget:assetOverview.knowledgeGraph.brandsAndProducts.brandAndProductRank'
          )}
        </div>
        <div className={styles.styledRankWrapper}>
          <div className={styles.rankContainer}>
            <div className={styles.rankLabel}>
              {t(
                'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
              )}
            </div>
            <div className={styles.rankFlexRow}>
              <TopRankIcon rank={relationship.rank} />
            </div>
          </div>
        </div>
      </div>
      <ErrorBoundaryWrapper>
        <GeoSourceEvidenceSection
          evidence={relationship.evidence ?? []}
          sourceEvidence={relationship.source_evidence ?? null}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
